import { useState } from 'react';
import {
  CheckIcon,
  CopyIcon,
  RocketLaunchIcon,
  ShieldCheckIcon,
  SignOutIcon,
  WalletIcon,
} from '@phosphor-icons/react/dist/ssr';

import { AuthLoadingScreen } from '@/components/templates';
import { ScreenShell } from '@/components/screen-shell';
import { NavBar } from '@/components/nav-bar';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardEyebrow,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { KeyValue } from '@/components/ui/key-value';
import { cn } from '@/lib/utils';

import { useAuth } from '../auth';
import { useT } from '../i18n';
import { useTelegramBackButton } from '../lib/telegram-back-button';
import { useNavStackEntry } from '../lib/nav-stack';
import { tryGetTelegram } from '../telegram';
import { isUsingWeb3Auth } from '../wallet';

const short = (a: string) => `${a.slice(0, 6)}…${a.slice(-4)}`;

export default function Deploy() {
  const auth = useAuth();
  const tg = tryGetTelegram();
  const t = useT();
  const [closeHint, setCloseHint] = useState(false);
  const [copied, setCopied] = useState(false);

  useTelegramBackButton();
  useNavStackEntry({ label: t('deploy.breadcrumb') });

  if (auth.state === 'loading') {
    return (
      <AuthLoadingScreen
        title={t('deploy.title')}
        description={t('deploy.creating')}
        shortDescription={t('deploy.creating')}
        steps={[
          { label: 'Telegram', color: 'bg-emerald-500' },
          { label: 'Wallet', color: 'bg-sky-500', delay: '0.4s' },
          { label: 'Ready', color: 'bg-primary', delay: '0.8s' },
        ]}
      />
    );
  }

  if (auth.state === 'error') {
    return (
      <ScreenShell eyebrow="Deploy" title={t('deploy.title')}>
        <Alert tone="danger">
          <AlertTitle>{t('deploy.init_failed')}</AlertTitle>
          <AlertDescription>{auth.error}</AlertDescription>
        </Alert>
      </ScreenShell>
    );
  }

  async function onCopy(addr: string) {
    try {
      await navigator.clipboard.writeText(addr);
      tg?.HapticFeedback?.notificationOccurred?.('success');
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      /* ignore */
    }
  }

  function onDone() {
    try {
      tg?.HapticFeedback?.notificationOccurred?.('success');
    } catch {
      /* ignore */
    }
    try {
      tg?.close?.();
    } catch {
      /* ignore */
    }
    setTimeout(() => {
      try {
        window.close();
      } catch {
        /* ignore */
      }
      setCloseHint(true);
    }, 400);
  }

  const backend = isUsingWeb3Auth()
    ? t('deploy.backend_web3auth')
    : t('deploy.backend_local');

  return (
    <ScreenShell
      eyebrow={
        <span className="inline-flex items-center gap-1.5">
          <RocketLaunchIcon weight="fill" className="size-3" />
          {t('deploy.title')}
        </span>
      }
      title={t('deploy.title')}
      description={t('deploy.ready_blurb')}
    >
      <NavBar />
      <Card>
        <CardHeader>
          <CardEyebrow>
            <WalletIcon weight="fill" className="size-3" />
            {t('deploy.your_address')}
          </CardEyebrow>
          <CardTitle className="font-mono text-base tracking-tight sm:text-lg">
            {short(auth.address)}
          </CardTitle>
          <CardDescription>
            <span className="inline-flex flex-wrap items-center gap-1.5">
              <ShieldCheckIcon weight="fill" className="size-3.5 text-primary" />
              {t('deploy.backed_by', { backend })}
            </span>
          </CardDescription>
        </CardHeader>

        <div className="flex flex-col gap-2 rounded-2xl bg-foreground/10 p-3 ring-1 ring-foreground/15">
          <code className="text-foreground block break-all font-mono text-[12px] leading-relaxed sm:text-[13px]">
            {auth.address}
          </code>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onCopy(auth.address)}
            className={cn(
              'self-start',
              copied && 'ring-emerald-500/40 text-emerald-300',
            )}
          >
            {copied ? (
              <CheckIcon weight="bold" className="size-4" />
            ) : (
              <CopyIcon weight="bold" className="size-4" />
            )}
            {copied ? t('common.copied') : t('common.copy_full_address')}
          </Button>
        </div>

        <CardContent>
          <KeyValue label="Network" value="Polygon" />
          <KeyValue
            label="Wallet provider"
            value={
              <Badge tone={isUsingWeb3Auth() ? 'success' : 'warning'}>
                {isUsingWeb3Auth() ? 'Web3Auth MPC' : 'Dev key'}
              </Badge>
            }
          />
        </CardContent>
      </Card>

      <Button size="xl" onClick={onDone}>
        <SignOutIcon weight="bold" className="size-5" />
        {t('deploy.done')}
      </Button>

      {closeHint && (
        <p className="text-muted-foreground text-center text-[12px]">
          {t('deploy.close_hint')}
        </p>
      )}
    </ScreenShell>
  );
}
